import React, { useState, useEffect, useRef } from 'react';
import { FaMoon, FaSun, FaBars, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './Header.css';

const Header = ({ toggleTheme, theme }) => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          !event.target.closest('.menu-toggle')) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
        return;
      }

      if (e.key === 'Tab') {
        const nav = menuRef.current;
        if (!nav) return;
        const focusable = nav.querySelectorAll('a, button');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    menuRef.current?.querySelector('a')?.focus();

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    closeMenu();
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <div className="header-content">
          <a href="#home" className="logo" onClick={closeMenu} aria-label="Home">
            MV.
          </a>

          <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`} ref={menuRef} aria-label="Main navigation">
            <a href="#home" onClick={closeMenu}>{t('header.home')}</a>
            <a href="#about" onClick={closeMenu}>{t('header.about')}</a>
            <a href="#skills" onClick={closeMenu}>{t('header.skills')}</a>
            <a href="#portfolio" onClick={closeMenu}>{t('header.portfolio')}</a>
            <a href="#experience" onClick={closeMenu}>{t('header.experience')}</a>
            <a href="#contact" onClick={closeMenu}>{t('header.contact')}</a>
            
            <div className="language-selector-mobile">
              <button className={i18n.language === 'es' ? 'active' : ''} onClick={() => changeLanguage('es')} aria-label="Español" aria-pressed={i18n.language === 'es'}>ES</button>
              <span>|</span>
              <button className={i18n.language === 'en' ? 'active' : ''} onClick={() => changeLanguage('en')} aria-label="English" aria-pressed={i18n.language === 'en'}>EN</button>
            </div>
          </nav>

          <div className="header-actions">
            <div className="language-selector">
              <button className={i18n.language === 'es' ? 'active' : ''} onClick={() => changeLanguage('es')} aria-label="Español" aria-pressed={i18n.language === 'es'}>ES</button>
              <button className={i18n.language === 'en' ? 'active' : ''} onClick={() => changeLanguage('en')} aria-label="English" aria-pressed={i18n.language === 'en'}>EN</button>
            </div>

            <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle Theme">
              {theme === 'light' ? <FaMoon size={16} /> : <FaSun size={16} />}
            </button>

            <button className="menu-toggle" onClick={toggleMenu} aria-label="Toggle Menu" aria-expanded={isMenuOpen}>
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;